/**
 * Blueprint recommendations panel
 * Shows infrastructure templates and Docker containers for download
 */

import { useState } from 'react';
import { useWizardState } from '@/hooks/useWizardState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Download, 
  Package, 
  Cloud, 
  FileCode, 
  Settings,
  Copy,
  CheckCircle2,
  Terminal,
  Cpu,
  HardDrive,
  Info,
  Rocket
} from 'lucide-react';
import { 
  generateBlueprints, 
  getDockerContainers, 
  downloadBlueprint,
  type Blueprint,
  type DockerContainer 
} from '@/lib/blueprints/generator';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { DeploymentWizardDialog } from '../deployment/DeploymentWizardDialog';
import type { DeploymentTarget } from '@/lib/deployment/types';

const ICON_MAP = {
  Package,
  Cloud,
  FileCode,
  Settings,
};

export function BlueprintRecommendations() {
  const { state } = useWizardState();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deploymentWizard, setDeploymentWizard] = useState<{
    open: boolean;
    blueprint: Blueprint | null;
    target: DeploymentTarget;
  }>({
    open: false,
    blueprint: null,
    target: 'docker-compose',
  });
  
  const blueprints = generateBlueprints(state);
  const containers = getDockerContainers(state);

  const handleDownload = (blueprint: Blueprint) => {
    downloadBlueprint(blueprint);
    toast({
      title: 'Blueprint Downloaded',
      description: `${blueprint.name} saved as ${blueprint.filename}`,
    });
  };

  const handleDeploy = (blueprint: Blueprint) => {
    setDeploymentWizard({
      open: true,
      blueprint,
      target: blueprint.format as DeploymentTarget,
    });
  };

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({
        title: 'Copied to Clipboard',
        description: 'Command copied successfully',
      });
    } catch (err) {
      toast({
        title: 'Copy Failed',
        description: 'Could not copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  if (blueprints.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Select models in the wizard to generate infrastructure blueprints.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {deploymentWizard.open && deploymentWizard.blueprint && (
        <DeploymentWizardDialog
          open={deploymentWizard.open}
          onOpenChange={(open) => setDeploymentWizard(prev => ({ ...prev, open }))}
          blueprint={deploymentWizard.blueprint}
          target={deploymentWizard.target}
        />
      )}

      <Tabs defaultValue="blueprints" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="blueprints">Infrastructure Blueprints</TabsTrigger>
          <TabsTrigger value="containers">Docker Containers</TabsTrigger>
        </TabsList>

        {/* Blueprints Tab */}
        <TabsContent value="blueprints" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Infrastructure Templates</CardTitle>
              <CardDescription>
                Download ready-to-deploy configuration files for your infrastructure
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {blueprints.map((blueprint) => {
                const IconComponent = ICON_MAP[blueprint.icon as keyof typeof ICON_MAP];
                
                return (
                  <Card key={blueprint.id} className="relative overflow-hidden">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <IconComponent className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{blueprint.name}</CardTitle>
                            <CardDescription className="text-xs mt-1">
                              {blueprint.description}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {blueprint.filename}
                        </Badge>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleDeploy(blueprint)}
                          className="flex-1"
                          size="sm"
                        >
                          <Rocket className="h-4 w-4 mr-2" />
                          Deploy
                        </Button>
                        <Button 
                          onClick={() => handleDownload(blueprint)} 
                          variant="outline"
                          size="sm"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Preview snippet */}
                      <div className="mt-2 p-3 rounded-md bg-muted/50 text-xs font-mono overflow-hidden">
                        <div className="line-clamp-3 text-muted-foreground">
                          {blueprint.content.split('\n').slice(0, 3).join('\n')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Docker Containers Tab */}
        <TabsContent value="containers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Model Containers</CardTitle>
              <CardDescription>
                Docker commands and requirements for your selected models
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {containers.map((container) => (
                <Card key={container.modelId} className="border-2">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{container.modelName}</CardTitle>
                        <CardDescription className="mt-1">
                          VLLM OpenAI-compatible server
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="gap-1">
                        <Package className="h-3 w-3" />
                        {container.imageUrl.split(':')[0]}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Requirements */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Cpu className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{container.requirements.gpus} GPU{container.requirements.gpus > 1 ? 's' : ''}</div>
                          <div className="text-xs text-muted-foreground">Required</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <HardDrive className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{container.requirements.memory}</div>
                          <div className="text-xs text-muted-foreground">RAM</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <HardDrive className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{container.requirements.storage}</div>
                          <div className="text-xs text-muted-foreground">Storage</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Docker Command */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Terminal className="h-4 w-4" />
                          Docker Run Command
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(container.dockerCommand, container.modelId)}
                        >
                          {copiedId === container.modelId ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="relative">
                        <pre className="p-3 rounded-md bg-muted text-xs font-mono overflow-x-auto">
                          <code>{container.dockerCommand}</code>
                        </pre>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Replace <code className="text-xs bg-muted px-1 py-0.5 rounded">your_token_here</code> with your Hugging Face token for model access.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
